import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { CreditCard } from "lucide-react";
import { formatDate } from "@tellescope/utils";
import { Badge } from "@tellescope/ui";

export interface PaymentsContextCardProps {
  purchaseId: string;
}

export function PaymentsContextCard({ purchaseId }: PaymentsContextCardProps) {
  const { t } = useTranslation("tickets");

  const purchaseQuery = useTellescopeQuery({
    model: "purchases",
    method: "getOne",
    request: purchaseId,
    enabled: !!purchaseId,
  });

  if (purchaseQuery.isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p className="text-sm">{t("Loading payment information...")}</p>
      </div>
    );
  }

  if (purchaseQuery.isError || !purchaseQuery.data) {
    return (
      <div className="p-4 border border-destructive rounded-lg">
        <p className="text-sm text-destructive">{t("Unable to load payment information. Please try again.")}</p>
      </div>
    );
  }

  const purchase = purchaseQuery.data;
  const isComplete = !!purchase.processedAt;
  const cost = Number(purchase.cost ?? 0);

  // Format amount with currency
  const formattedAmount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cost / 100); // Assuming cost is in cents

  return (
    <div className="space-y-2 p-4 border border-gray-200 rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <CreditCard className="h-4 w-4 text-gray-600 flex-shrink-0" />
          <h3 className="text-sm font-medium text-gray-900 truncate">{t("Payment Information")}</h3>
        </div>
        <Badge variant={isComplete ? "default" : "secondary"} className="text-xs">
          {isComplete ? t("Complete") : t("Pending")}
        </Badge>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between border-b border-gray-200 pb-3">
          <span className="text-lg font-bold text-gray-900">{formattedAmount}</span>
          {purchase.processedAt && <span className="text-xs text-gray-500">{formatDate(purchase.processedAt)}</span>}
        </div>

        {purchase.title && (
          <div className="flex items-start gap-2">
            <span className="text-sm font-medium text-gray-600 w-24 flex-shrink-0">{t("Product:")}</span>
            <span className="text-sm text-gray-900 flex-1">{purchase.title}</span>
          </div>
        )}

        {purchase.description && (
          <div className="flex items-start gap-2">
            <span className="text-sm font-medium text-gray-600 w-24 flex-shrink-0">{t("Description:")}</span>
            <p className="text-sm text-gray-900 whitespace-pre-wrap flex-1">{purchase.description}</p>
          </div>
        )}

        {purchase.processor === "Stripe" && purchase.externalId && (
          <div className="flex items-start gap-2">
            <span className="text-sm font-medium text-gray-600 w-24 flex-shrink-0">{t("Transaction:")}</span>
            <span className="text-xs text-gray-700 font-mono flex-1 break-all">{purchase.externalId}</span>
          </div>
        )}

        <div className="flex items-start gap-2">
          <span className="text-sm font-medium text-gray-600 w-24 flex-shrink-0">{t("Created:")}</span>
          <span className="text-sm text-gray-900">{formatDate(purchase.createdAt)}</span>
        </div>

        {purchase.notes && (
          <div className="flex items-start gap-2">
            <span className="text-sm font-medium text-gray-600 w-24 flex-shrink-0">{t("Notes:")}</span>
            <p className="text-sm text-gray-900 whitespace-pre-wrap flex-1">{purchase.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
